import { defineStore } from 'pinia'
import { partitionAllowedFiles } from '~~/shared/allowedFileTypes'
import { useCasesStore } from '~/stores/cases'
import { useClientsStore } from '~/stores/clients'
import { useDocumentsStore } from '~/stores/documents'
import { useDocumentsIndexingStore, type IndexSource } from '~/stores/documentsIndexing'
import type { UploadedItem } from '~~/shared/items/uploadedItem'
import { useWebChatStore } from '~/stores/web-chat'
import { planAllows } from '~/composables/usePlanFeature'

/**
 * THE upload-target store (AlexAmin/marcus#83). One place the question "where does this document
 * belong?" is asked and answered.
 *
 * ## Why one store and not five call sites
 *
 * Four surfaces start an upload — the finder page, the sidebar's file explorer, the icon rail's
 * "new" menu, and a drop onto a library folder. Every one of them called
 * `indexing.upload(files, { path })` and stopped there, which is exactly how a document ends up
 * attached to nothing: the upload path knows about FOLDERS, and a folder is not a client, a matter
 * or a conversation. Teaching each surface to resolve a target separately would give four slightly
 * different answers to one product question and guarantee that the fifth surface forgets. So the
 * resolution, the pending state and the write all live here, and every surface calls one entry
 * point.
 *
 * There was a fifth: a window-level native drop that caught everything else. It is gone (#83), and
 * a zone that wants a drop now owns it — and owns it through this store, not through
 * `indexing.upload` directly.
 *
 * ## One entry point, because a dismissed dialog must not leave bytes behind
 *
 * `requestUpload` — ASK, THEN UPLOAD, for clicks AND for drops. Nothing is sent until a target
 * exists, so dismissing the picker uploads nothing and no document is ever created without a home.
 * It opens PRE-SELECTED wherever the route already answers the question (a client page, a matter
 * page, an open conversation): the picker is a confirmation in context, not an interrogation.
 *
 * There used to be a second entry point, `acceptDroppedFiles` — UPLOAD, THEN ASK — on the argument
 * that a native drop is a completed gesture and must never be bounced. It was the last way to make
 * an unattached document: the bytes were already in the library, so a dismissed dialog left them
 * filed nowhere, which is "accept, then ask" surviving in the one place #83 had not reached. Both
 * of its callers (`useFolderDrop`, the documents sidebar) now ask first. **The cost is named
 * honestly: a dismissed drop uploads nothing and the user re-drops.** That loses no bytes — the
 * file never left their disk — where the old shape lost the document's home instead.
 *
 * The picker still has a question to ask on a folder drop: a folder is a place in the tree, not a
 * client, a matter, a conversation or a sheet, so `path` and the target are orthogonal (below).
 *
 * `requestUpload` RESOLVES with the rows it created, so a caller that needs the uploaded ids — the
 * batch party table, which reads identity documents out of them — can await the answer instead of
 * calling `indexing.upload` behind the store's back, which was the other way an upload escaped
 * #83. It resolves with `[]` when the dialog is dismissed.
 *
 * ## What is deliberately NOT here
 *
 * No SILENT fallback to "no client". Routing a plan-with-clients upload there unasked would rebuild the unattached pile under a friendlier name, which is the exact
 * thing #83 abolishes. There is nothing to fall back TO any more: a dismissed dialog has uploaded
 * nothing, so there is no document to file somewhere untrue.
 *
 * ## The one plan where it is the answer (MAR-1006, decided by Alex 2026-10-01)
 *
 * A plan without case management — Light — cannot create a client (`/api/clients` rides that gate),
 * so "every document belongs to a client" left its library upload with no answer at all. There, and
 * only there, the dialog offers `{ kind: 'noClient' }` — "Sin cliente" — and pre-selects it when the
 * route answers nothing else. It is an explicit answer, not a fallback, and it reuses the existing
 * path rather than building another: the document is written with NO edge — attached to nothing,
 * counted in `listOrgDocuments`' `unattachedTotal` and rendered in "Agrupar por: Clientes"' virtual
 * "Sin cliente" group by `useDocumentGroups` (`unattachedGroup`, MAR-1006 part 2). No client is created
 * for a Light org, and nothing here writes a `client_documents` row. `confirm` refuses the target on a plan WITH case
 * management, so the option cannot leak upward even if something selects it by hand.
 */

/** Where an upload can land. Client is the coarsest acceptable answer — a user who does not yet know
 *  the matter can still say whose it is. */
export type UploadTarget =
  | { kind: 'case'; id: string; name: string }
  | { kind: 'client'; id: string; name: string }
  | { kind: 'conversation'; id: string; name: string }
  /** No client: attached to nothing, shown under "Sin cliente". Offered only without case management (MAR-1006). */
  | { kind: 'noClient' }

const NO_CLIENT_TARGET: UploadTarget = { kind: 'noClient' }

export const useUploadTargetStore = defineStore('uploadTarget', () => {
  const router = useRouter()
  const casesStore = useCasesStore()
  const clientsStore = useClientsStore()
  const documents = useDocumentsStore()
  const indexing = useDocumentsIndexingStore()
  const webChat = useWebChatStore()

  const open = ref(false)
  /** Files awaiting a target; empty once they have been uploaded by `confirm`. */
  const queuedFiles = ref<File[]>([])
  /** Library rows awaiting their edge — set by `confirm` once the upload has run. */
  const pending = ref<Array<{ id: string; name: string; file_name?: string | null; status?: string | null }>>([])
  const selected = ref<UploadTarget | null>(null)
  /**
   * Whether this plan files without a client (MAR-1006): true exactly when the organization has no
   * case management. Resolved from the live profile each time the dialog opens, so a plan changed by
   * an operator is followed at the next upload. `false` until it is known, so the option never
   * flashes on a plan that has clients.
   */
  const noClientAvailable = ref(false)
  /** Bumped per `requestUpload`, so a plan answer that arrives after the dialog moved on is dropped. */
  let request = 0
  const busy = ref(false)
  /**
   * A CODE, not a sentence. The store must not build user-facing copy — the dialog owns the
   * translation, and a message assembled here would be untranslatable and English-only.
   * `'upload'` = the upload itself failed; `'link'` = it uploaded but some rows would not file, and
   * `errorNames` says which; `'unsupported'` = `requestUpload` was handed one or more files whose
   * type is not accepted, and `errorNames` says which — see `requestUpload` (AlexAmin/marcus#804).
   */
  const error = ref<'' | 'upload' | 'link' | 'unsupported'>('')
  const errorNames = ref<string[]>([])
  /** The library folder the bytes land in. Orthogonal to the target: a document filed under a client
   *  still lives somewhere in the folder tree, and a drop onto a folder must respect that folder. */
  const path = ref('/')
  const source = ref<IndexSource>('library')
  /**
   * Settles the promise `requestUpload` handed its caller. Deliberately a plain `let`, not a ref:
   * it is a continuation, not state anything renders, and putting a function in a ref makes Pinia
   * devtools try to serialise it.
   */
  let settle: ((items: UploadedItem[]) => void) | null = null
  /** The rows the upload created, kept for the caller's promise. Not `pending`, which is trimmed to
   *  what the dialog renders and drops the `duplicate` flag a caller may need. */
  let uploaded: UploadedItem[] = []

  /** Answer the caller exactly once — a second call after the first would leave it awaiting forever
   *  or resolve it twice, and both have to be impossible for `confirm`/`dismiss` to be safe. */
  function finish(items: UploadedItem[]): void {
    const resolve = settle
    settle = null
    resolve?.(items)
  }

  /** How many documents the open dialog is deciding for — files not yet up, or rows already up. */
  const count = computed(() => (queuedFiles.value.length || pending.value.length))
  const names = computed(() =>
    queuedFiles.value.length ? queuedFiles.value.map(f => f.name) : pending.value.map(p => p.name))

  /**
   * The target the current context already implies, or null.
   *
   * Read at open time rather than held as a computed: the dialog must not retarget itself because a
   * background navigation changed the route while it is on screen.
   */
  function resolveRouteTarget(): UploadTarget | null {
    const route = router.currentRoute.value
    const caseMatch = /^\/app\/cases\/([^/]+)/.exec(route.path)
    if (caseMatch?.[1]) {
      const id = caseMatch[1]
      return { kind: 'case', id, name: casesStore.byId.get(id)?.name ?? '' }
    }
    const clientMatch = /^\/app\/clients\/([^/]+)/.exec(route.path)
    if (clientMatch?.[1]) {
      const id = clientMatch[1]
      return { kind: 'client', id, name: clientsStore.byId.get(id)?.name ?? '' }
    }
    // An open thread is a real home under #83 (`conversation_documents`), and on the chat surface it
    // is the only one the context offers. A brand-new chat has no conversation row yet, so there is
    // nothing to link to and the user is asked instead.
    if (webChat.conversationId) {
      return { kind: 'conversation', id: webChat.conversationId, name: webChat.conversationTitle ?? '' }
    }
    return null
  }

  function reset() {
    queuedFiles.value = []
    pending.value = []
    selected.value = null
    error.value = ''
    errorNames.value = []
    noClientAvailable.value = false
    busy.value = false
    path.value = '/'
    source.value = 'library'
    uploaded = []
  }

  /**
   * ASK, THEN UPLOAD — every entry point in the app: the finder, the file explorer, the icon rail,
   * a drop onto a folder row, a drop onto the documents sidebar, the batch party table, and
   * `DocumentPicker`'s "no compatible documents" empty state.
   *
   * The work happens in `confirm`, which refuses to run without a target — so none of them can
   * produce an unattached document.
   *
   * Resolves with the rows the upload created, or `[]` if the dialog was dismissed or every file
   * was of a type we do not accept. Callers that only start the upload can ignore it.
   *
   * **A disallowed file is never just dropped (#804).** Every entry point used to run its own
   * `files.filter(f => isAllowedFile(f.name))` and throw the rest away — a `.zip`, say, vanished
   * with no dialog, no toast, and nothing in the console. This is the one place that filtering
   * happens now, so the dialog can always name what it rejected: `error`/`errorNames` are set to
   * `'unsupported'` and the disallowed filenames whenever any file fails the check, whether or not
   * some others in the same batch were valid. When NOTHING in the batch is valid the dialog still
   * opens — with nothing queued to pick a target for — purely to show that message; `count === 0`
   * is what the dialog reads to hide the target picker in that case.
   */
  function requestUpload(
    files: File[], opts: { path?: string; source?: IndexSource; target?: UploadTarget } = {},
  ): Promise<UploadedItem[]> {
    if (!files.length) return Promise.resolve([])
    const { valid, disallowed } = partitionAllowedFiles(files)
    // A second request while one is open replaces it, so the first caller is answered rather than
    // left awaiting a dialog that no longer represents its files.
    finish([])
    reset()
    if (disallowed.length) {
      error.value = 'unsupported'
      errorNames.value = disallowed.map(f => f.name)
    }
    if (!valid.length) {
      open.value = true
      return new Promise<UploadedItem[]>((resolve) => { settle = resolve })
    }
    queuedFiles.value = valid
    path.value = opts.path ?? '/'
    source.value = opts.source ?? 'library'
    // A caller that already knows what it is filing FOR (a case-scoped `DocumentPicker`) states it
    // outright: it is picking for that case whether or not the route happens to name it, and the
    // route is the fallback question, not the better answer.
    selected.value = opts.target ?? resolveRouteTarget()
    open.value = true
    void resolvePlan()
    return new Promise<UploadedItem[]>((resolve) => { settle = resolve })
  }

  /** Learn whether this plan files without a client, and pre-select that home when nothing else
   *  answered the question — on Light it is the only answer a library upload has. */
  async function resolvePlan(): Promise<void> {
    const mine = ++request
    let available = false
    try {
      available = !(await planAllows('case_management'))
    } catch (err) {
      console.error('[marcus:upload-target] could not read the plan:', err)
    }
    if (mine !== request || !open.value) return
    noClientAvailable.value = available
    if (available && selected.value === null) selected.value = NO_CLIENT_TARGET
  }

  function choose(target: UploadTarget | null): void {
    selected.value = target
    // 'unsupported' describes the FILES, not the target — picking one doesn't make the rejected
    // ones any more valid, so it stays on screen until the dialog closes. A stale 'upload'/'link'
    // failure from a previous attempt is what this clears.
    if (error.value !== 'unsupported') error.value = ''
  }

  /** Attach one already-uploaded row to the chosen target, mirroring the edge into the store that
   *  owns it so the browser re-derives without a refetch. */
  async function link(
    target: UploadTarget,
    row: { id: string; name: string; file_name?: string | null; status?: string | null },
  ): Promise<void> {
    if (target.kind === 'case') {
      await casesStore.addCaseDocument(target.id, row.id, '/')
      return
    }
    if (target.kind === 'client') {
      await clientsStore.addClientDocument(target.id, row.id)
      documents.linkToClient(target.id, row.id)
      return
    }
    // No edge, on purpose (MAR-1006): attached to nothing is exactly what the "Sin cliente" group
    // shows. Writing a client link here would need a client in an organization whose plan has none.
    if (target.kind === 'noClient') return
    // `attachDocument` writes `conversation_documents` itself (fire-and-forget) AND puts the chip in
    // the composer, which is what the user expects from a file dropped into a chat.
    webChat.attachDocument({
      id: row.id, name: row.name, file_name: row.file_name ?? null, status: row.status ?? null,
    })
  }

  /**
   * File the documents where the user said.
   *
   * The upload happens HERE, so a cancelled dialog uploads nothing at all. On a link failure the
   * rows survive in `pending` and the dialog stays open, so pressing save again re-files them
   * rather than uploading a second copy.
   */
  async function confirm(): Promise<void> {
    const target = selected.value
    if (!target || busy.value) return
    // A plan with clients never files a document under nothing (MAR-1006): the option is not offered
    // there, and this keeps a hand-made selection from getting past that.
    if (target.kind === 'noClient' && !noClientAvailable.value) return
    busy.value = true
    error.value = ''
    try {
      if (queuedFiles.value.length) {
        const created = await indexing.upload(queuedFiles.value, { path: path.value, source: source.value })
        queuedFiles.value = []
        uploaded = created.filter(item => !!item?.id)
        pending.value = created
          .filter(item => !!item?.id)
          .map(item => ({
            id: item.id, name: item.name ?? item.file_name ?? item.id,
            file_name: item.file_name, status: item.status,
          }))
      }
      const unlinked: string[] = []
      for (const row of pending.value) {
        try {
          await link(target, row)
        } catch (err) {
          // Reported, never swallowed: an upload that was never linked is indistinguishable from a
          // successful one — the file is in the library, silently unfiled, which is the state this
          // ticket exists to make unreachable.
          unlinked.push(row.name)
          console.error('[marcus:upload-target] could not file document:', row.id, err)
        }
      }
      await documents.refreshAll()
      if (unlinked.length) {
        error.value = 'link'
        errorNames.value = unlinked
        return
      }
      const created = uploaded
      open.value = false
      reset()
      finish(created)
    } catch (err) {
      console.error('[marcus:upload-target] upload failed:', err)
      error.value = 'upload'
    } finally {
      busy.value = false
    }
  }

  /**
   * Close without filing. Nothing was uploaded, so this is a plain cancel and no document is left
   * behind without a home — which is the whole reason the upload waits for `confirm` (#83).
   *
   * The caller's promise resolves with whatever DID get uploaded, which is empty except on the one
   * path where it is not: an upload that succeeded and whose linking failed leaves the rows in the
   * library and the dialog open on an error. Answering `[]` there would tell the caller no document
   * exists when one does.
   */
  function dismiss(): void {
    const created = uploaded
    open.value = false
    reset()
    finish(created)
  }

  return {
    open, queuedFiles, pending, selected, noClientAvailable, busy, error, errorNames, path, count, names,
    resolveRouteTarget, requestUpload, choose, confirm, dismiss,
  }
})
